import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { DatePipe } from '@angular/common';
import { AppService } from '@services/app.service';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { BillingDetails } from '@/models/billing/BillingDetails';
import { Subject, takeUntil } from 'rxjs';
import { ProductVariantDetails } from '@/models/product-variant-details/product-variant-details';

@Component({
  selector: 'app-report-details',
  templateUrl: './report-details.component.html',
  styleUrl: './report-details.component.scss'
})
export class ReportDetailsComponent implements OnInit {
  destroy$: Subject<boolean> = new Subject<boolean>();
  getAllShopBillDetails: BillingDetails[] = [];
  getAllVariantDetails: ProductVariantDetails[] = [];
  fromDate!: string;
  toDate!: string;
  todayMax!: string;
  stockFromDate!: string;
  stockToDate!: string;

  stockStartDate = 0;
  stockEndDate = 0;

  startDate: number = 0;
  endDate: number = 0;

  showStockTable = false;
  showSalesTable = false;

  constructor(private appService: AppService, private toastr: ToastrService, private spinner: NgxSpinnerService,
    private datePipe: DatePipe,) { }

  ngOnInit(): void {
    this.todayMax = this.formatDate(new Date());
    this.setLastWeekDates();
    this.setLastWeekStockDates();
    this.getBillingDetailsByStartAndEndDate();
    this.getProductVariantDetails();
  }

  // Auto set last 7 days
  setLastWeekDates() {
    const today = new Date();
    const lastWeek = new Date();

    lastWeek.setDate(today.getDate() - 7);

    this.fromDate = this.formatDate(lastWeek);
    this.toDate = this.formatDate(today);

    this.convertToEpoch();
  }

  setLastWeekStockDates() {

    const today = new Date();

    const lastWeek = new Date();

    lastWeek.setDate(today.getDate() - 7);

    this.stockFromDate = this.formatDate(lastWeek);

    this.stockToDate = this.formatDate(today);

    this.convertStockDateToEpoch();

  }

  convertStockDateToEpoch() {

    this.stockStartDate =
      new Date(this.stockFromDate).getTime();

    this.stockEndDate =
      new Date(this.stockToDate).getTime();

  }

  onDate() {

    if (!this.fromDate || !this.toDate) return;

    if (new Date(this.fromDate) > new Date(this.toDate)) {
      this.toastr.error('Start date should not be greater than End date');
      return;
    }

    this.convertToEpoch();
    this.getBillingDetailsByStartAndEndDate();
  }

  // format yyyy-MM-dd
  formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  convertToEpoch() {
    this.startDate = new Date(this.fromDate).getTime();
    this.endDate = new Date(this.toDate).getTime();
  }

  getBillingDetailsByStartAndEndDate() {
    this.spinner.show();
    this.appService.getBillingDetailsByStartAndEndDate(this.startDate, this.endDate)
      .subscribe({
        next: (res) => {
          this.spinner.hide();
          this.getAllShopBillDetails = res.sort((a, b) => b.billId - a.billId);
        },
        error: (err) => {
          this.spinner.hide();
          this.toastr.error('Failed to load billing data');
          console.error(err);
        }
      });
  }

  onStockDateChange() {

    if (!this.stockFromDate || !this.stockToDate) {
      return;
    }

    if (new Date(this.stockFromDate) > new Date(this.stockToDate)) {

      this.toastr.error(
        'From Date should not be greater than To Date.'
      );

      return;
    }

    this.convertStockDateToEpoch();

  }

  viewCurrentSalesReport() {
    this.showSalesTable = true;
    this.getBillingDetailsByStartAndEndDate();
  }

  downloadDailySalesReport() {
    let data: any[] = [];
    this.getAllShopBillDetails.forEach((bill, index) => {
      const totalItems = bill?.products?.length || 0;
      data.push({
        'S.No': index + 1,
        'Invoice No': bill?.invoiceNumber || "-",
        'Date': bill?.invoiceDate || "-",
        'Customer Name': bill?.customerName || "-",
        'Phone': bill?.phoneNumber || "-",
        'City': bill?.city || "-",
        'State': bill?.state || "-",
        'Tracking No': bill?.trackingNumber || "-",
        'Weight': bill?.weight || "-",
        'Bill Type': bill?.billingType || "-",
        'Subtotal': this.formatIndianCurrency(bill?.subTotal),
        'Product Discount': this.formatIndianCurrency(bill?.discount),
        'Manual Discount': this.formatIndianCurrency(bill?.manualDiscountAmount),
        'Total Amount': this.formatIndianCurrency(bill?.totalAmount),
        'Balance': this.formatIndianCurrency(bill?.dueAmount),
        'Payment Method': bill?.paymentMethod || "-",
        'Total Items': totalItems,
        'Shipping Partner': bill?.shippingPartner || "-",
        'Transaction Ref': bill?.transactionRefId || "-",
        'Shipping Amount': this.formatIndianCurrency(bill?.shippingAmount),
        'Status': bill?.billingStatus || '-',
        'Remarks': bill?.note || '-'
      });

    });
    this.exportToExcel(data, `Daily_Sales_${this.fromDate}_to_${this.toDate}`);
  }

  // get product details
  // getProductVariantDetails() {
  //   this.spinner.show();
  //   this.appService.getAllWithOutZeroQtyVariant()
  //     .pipe(takeUntil(this.destroy$))
  //     .subscribe((data: ProductVariantDetails[]) => {
  //       this.spinner.hide();
  //       this.getAllVariantDetails = data.sort((a, b) => b.productVariantId - a.productVariantId);
  //       this.downloadCurrentStockReport();
  //     }, (error: any) => {
  //       this.spinner.hide();
  //       this.toastr.error(error.message || "Something went wrong, please try again...")
  //     });
  // }

  viewCurrentStockReport() {
    this.showStockTable = true;
    this.getProductVariantDetails();
  }

  getProductVariantDetails() {
    this.spinner.show();
    this.appService
      .getAllWithZeroQtyVariant(
        this.stockStartDate,
        this.stockEndDate
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: ProductVariantDetails[]) => {
          this.spinner.hide();
          this.getAllVariantDetails =
            data.sort(
              (a, b) =>
                b.productVariantId - a.productVariantId
            );
        },
        error: (error) => {
          this.spinner.hide();
          this.toastr.error(
            error?.error?.message ||
            'Unable to load current stock report.'
          );
        }
      });
  }

  downloadCurrentStockReport() {
    const data = this.getAllVariantDetails.map((item, index) => ({
      'S.No': index + 1,
      'Product Name': item.productName || "-",
      'Barcode': item.barcodeNumber || "-",
      'Size': item.size || "-",
      'SRP': this.formatIndianCurrency(item.productSrp),
      'Current Stock': item.availableStock || 0,
      'Date': item.updatedAt
      ? this.datePipe.transform(item.updatedAt, 'dd-MM-yyyy')
      : "-",
    }));

    this.exportToExcel(data, 'Current_Stock_Report');

  }


  exportToExcel(jsonData: any[], fileName: string) {

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);

    // Auto column width
    const colWidths = Object.keys(jsonData[0] || {}).map(key => ({
      wch: Math.max(key.length, 15)
    }));

    worksheet['!cols'] = colWidths;

    const workbook: XLSX.WorkBook = {
      Sheets: { 'Report': worksheet },
      SheetNames: ['Report']
    };

    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array'
    });

    this.saveAsExcelFile(excelBuffer, fileName);
  }

  // =====================================================
  // 4. SAVE FILE
  // =====================================================

  saveAsExcelFile(buffer: any, fileName: string) {

    const data: Blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    saveAs(data, `${fileName}.xlsx`);
  }

  formatIndianCurrency(value: any): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(Number(value || 0));
  }

}
